import createClient from '@eid-easy/eideasy-browser-client';
import {
	ApiWrapperError,
	isStandardBackendError,
} from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignerContactType } from '@dealstack/shared/lib/generated';

/** The eID Easy browser-client instance returned by `createClient`. */
export type EidEasySdkClient = ReturnType<typeof createClient>;

/**
 * eID Easy "Nordic package" OAuth2 redirect methods that launch straight into
 * the national bank/app via the browser SDK. Each maps to the SDK launcher name
 * and the country code eID Easy uses to route to the right provider. Shared by
 * the in-app and public signing hooks.
 */
export const REDIRECT_METHODS: Partial<
	Record<
		EidEasySigningMethod,
		{
			launcher: 'seBankIdSignature' | 'noBankIdAdvancedHashSignature' | 'mitIdSignature';
			countryCode: string;
		}
	>
> = {
	[EidEasySigningMethod.SE_BANKID]: { launcher: 'seBankIdSignature', countryCode: 'SE' },
	// Norwegian BankID's direct-PDF variant (`no-bankid-adv-signature`) requires
	// the signer's legal name, and the lighter `no-bankid-adv-light-signature`
	// doesn't support the `pdf` container at all. We use the advanced-hash variant
	// instead: a pure OAuth redirect (like Swedish BankID / MitID) that needs no
	// name and signs a detached digest the backend embeds into the PDF via PAdES.
	[EidEasySigningMethod.NORWEGIAN_BANKID]: {
		launcher: 'noBankIdAdvancedHashSignature',
		countryCode: 'NO',
	},
	[EidEasySigningMethod.MITID]: { launcher: 'mitIdSignature', countryCode: 'DK' },
};

/**
 * Known Smart-ID / eID Easy end-state codes returned by the browser client's
 * `fail` callback.  Keys are the raw codes; values are i18n keys under the
 * `eid_easy_signing` namespace.
 *
 * @see https://sk-eid.github.io/smart-id-documentation/test_accounts.html
 */
const SMART_ID_ERROR_KEYS: Record<string, string> = {
	USER_REFUSED: 'eid_easy_signing.error_user_refused',
	USER_REFUSED_INTERACTION: 'eid_easy_signing.error_user_refused',
	USER_REFUSED_DISPLAYTEXTANDPIN: 'eid_easy_signing.error_user_refused',
	USER_REFUSED_VC_CHOICE: 'eid_easy_signing.error_user_refused',
	USER_REFUSED_CONFIRMATIONMESSAGE: 'eid_easy_signing.error_user_refused',
	USER_REFUSED_CONFIRMATIONMESSAGE_WITH_VC_CHOICE: 'eid_easy_signing.error_user_refused',
	WRONG_VC: 'eid_easy_signing.error_wrong_vc',
	TIMEOUT: 'eid_easy_signing.error_timeout',
};

/**
 * Popup errors mapped to i18n keys. These arise from the FTN popup
 * signing flow when the browser blocks the popup or the user closes it.
 */
const POPUP_ERROR_KEYS: Record<string, string> = {
	popup_blocked: 'eid_easy_signing.error_popup_blocked',
	popup_closed: 'eid_easy_signing.error_signing_cancelled',
};

/**
 * Backend `BusinessException` codes mapped to i18n keys. Preferred over the message
 * heuristics below: a domain 400 arrives as `{ error: { code, message } }`, and the
 * message is prose the client must not pattern-match on. Plain `BadRequestException`s
 * carry no code and still land on `error_generic`.
 */
/**
 * The key 3010104 maps to. Exported because the portal keys its stale-selection recovery off
 * it, and a second copy there would silently stop matching if this one were ever renamed.
 */
export const DOCUMENT_NOT_BULK_SIGNABLE_ERROR_KEY =
	'eid_easy_signing.error_document_not_bulk_signable';

const BUSINESS_ERROR_KEYS: Record<number, string> = {
	[ERROR_CODES.GENERIC.CONTRACTS.SIGNING[3010102]]:
		'eid_easy_signing.error_witness_required_bulk',
	// Raised for any explicit selection past one batch. A portal signatory who sends no
	// selection is not subject to it — their overflow defers instead.
	[ERROR_CODES.GENERIC.CONTRACTS.SIGNING[3010103]]: 'eid_easy_signing.error_too_many_documents',
	[ERROR_CODES.GENERIC.CONTRACTS.SIGNING[3010104]]: DOCUMENT_NOT_BULK_SIGNABLE_ERROR_KEY,
};

/**
 * HTTP status codes mapped to user-friendly i18n keys. 409 is the signing concurrency fence — the
 * document moved while this signer held a prepared session; re-preparing is the fix, which is what
 * the error step's "try again" does.
 */
const HTTP_STATUS_ERROR_KEYS: Record<number, string> = {
	403: 'eid_easy_signing.error_no_permission',
	404: 'eid_easy_signing.error_session_expired',
	409: 'eid_easy_signing.error_document_changed',
	429: 'eid_easy_signing.error_rate_limited',
};

/**
 * Attempts to detect a known Smart-ID error code in a raw error message string.
 * The eID Easy browser client passes the end-state code (e.g. "USER_REFUSED")
 * either as the full message or embedded in a longer string.
 */
const detectSmartIdCode = (message: string): string | undefined => {
	const upper = message.toUpperCase().trim();
	for (const code of Object.keys(SMART_ID_ERROR_KEYS)) {
		if (upper === code || upper.includes(code)) {
			return code;
		}
	}
	return undefined;
};

/**
 * Detects popup/new-tab errors from the redirect signing flows.
 * Returns a key into POPUP_ERROR_KEYS, or undefined.
 */
const detectPopupError = (message: string): string | undefined => {
	const lower = message.toLowerCase();
	if (
		lower.includes('tab blocked') ||
		lower.includes('popup blocked') ||
		lower.includes('popup was blocked')
	) {
		return 'popup_blocked';
	}
	if (lower.includes('popup was closed') || lower.includes('signing cancelled')) {
		return 'popup_closed';
	}
	return undefined;
};

/**
 * Attempts to extract an HTTP status code from common error message patterns:
 *  - "Request failed with status code 404"
 *  - "Public signing API error: 404"
 */
const detectHttpStatus = (message: string): number | undefined => {
	// Matches patterns like "status code: 404", "status 404", "status=404".
	// Uses bounded quantifiers ({0,5}) instead of \s* to prevent ReDoS backtracking.
	const match = message.match(/(?:status\s{0,5}(?:code)?\s{0,5}[:=]?\s{0,5})(\d{3})/i);
	return match?.[1] ? Number(match[1]) : undefined;
};

/**
 * Translates a raw error into a translation key that the modal can pass
 * through `tCommonUi()`.  Returns a key string — never a raw technical message.
 */
export const toErrorKey = (err: unknown): string => {
	// A BusinessException code is the most specific signal available, and unlike the
	// message it survives translation — so it outranks the status and the regexes below.
	if (isStandardBackendError(err) && BUSINESS_ERROR_KEYS[err.error.code]) {
		return BUSINESS_ERROR_KEYS[err.error.code];
	}

	// Prefer the structured HTTP status when present (Task 3 populates it) — the
	// message-regex fallback below only sees statuses embedded in a string.
	if (err instanceof ApiWrapperError && err.status && HTTP_STATUS_ERROR_KEYS[err.status]) {
		return HTTP_STATUS_ERROR_KEYS[err.status];
	}

	const rawMessage = extractRawMessage(err);

	if (rawMessage) {
		// 1. Pass-through: callers can throw `Error('eid_easy_signing.foo')`
		// (e.g. timeout helpers) and have it surface verbatim.
		if (rawMessage.startsWith('eid_easy_signing.')) {
			return rawMessage;
		}

		// 2. Smart-ID specific codes
		const smartIdCode = detectSmartIdCode(rawMessage);
		if (smartIdCode) {
			return SMART_ID_ERROR_KEYS[smartIdCode] ?? 'eid_easy_signing.error_generic';
		}

		// 3. Popup errors (FTN flow)
		const popupCode = detectPopupError(rawMessage);
		if (popupCode) {
			return POPUP_ERROR_KEYS[popupCode] ?? 'eid_easy_signing.error_generic';
		}

		// 4. HTTP status codes
		const httpStatus = detectHttpStatus(rawMessage);
		if (httpStatus && HTTP_STATUS_ERROR_KEYS[httpStatus]) {
			return HTTP_STATUS_ERROR_KEYS[httpStatus];
		}
	}

	// 5. Fallback
	return 'eid_easy_signing.error_generic';
};

export const extractRawMessage = (err: unknown): string | undefined => {
	// Checked before `Error`, which ApiWrapperError now extends. Its own message
	// describes the transport failure ("API request failed (404)"), which the
	// substring heuristics below would mis-read as a signing outcome; `toErrorKey`
	// reads `status` structurally instead.
	if (err instanceof ApiWrapperError) {
		return typeof err.error === 'string' ? err.error : undefined;
	}
	if (err instanceof Error) {
		return err.message;
	}
	if (typeof err === 'string') {
		return err;
	}
	return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

/**
 * Normalises an unknown thrown value or SDK `fail` payload into an Error,
 * reusing `extractRawMessage` for the message. Returns the value unchanged when
 * it is already an Error (preserving its stack).
 *
 * eID Easy's reason is appended when present. The SDK hands `fail` an AxiosError
 * whose `message` is only "Request failed with status code N", but copies the
 * body's `message` onto `userMessage` first — without grafting that on, every
 * eID Easy failure reached Sentry with an identical, contentless message and a
 * wrong OTP code looked the same as a rejected payload (DC-1072).
 */
export const ensureError = (err: unknown): Error => {
	if (err instanceof Error) {
		const reason =
			isRecord(err) && typeof err.userMessage === 'string' && err.userMessage.trim();
		if (reason) {
			err.message = `${err.message}: ${reason}`;
		}
		return err;
	}
	return new Error(extractRawMessage(err) ?? String(err));
};

/** Max wrong OTP entries before the flow escalates to the fatal error screen. */
export const MAX_OTP_ATTEMPTS = 5;

/** Shown when eID Easy accepted the code but could not sign the document. */
const DOCUMENT_SIGNING_FAILED_ERROR_KEY = 'eid_easy_signing.error_document_signing_failed';

/** The HTTP status of an SDK failure: the AxiosError's response, else one named in the message. */
const otpFailureStatus = (err: unknown): number | undefined => {
	if (isRecord(err) && isRecord(err.response) && typeof err.response.status === 'number') {
		return err.response.status;
	}
	const rawMessage = extractRawMessage(err);
	return rawMessage ? detectHttpStatus(rawMessage) : undefined;
};

/**
 * Decides what a flow should do with a caught OTP submit error.
 * `attemptsSoFar` is the count of wrong entries BEFORE this one.
 *
 * eID Easy checks the code and signs the PDF in the same call, so only its validation
 * rejection (400/422) means a wrong or expired code. A 5xx is the signing itself failing
 * (e.g. an encrypted PDF, DC-1206) and must not be reported as the signer's mistake.
 */
export function classifyOtpSubmitFailure(
	err: unknown,
	attemptsSoFar: number
): { fatal: boolean; errorKey: string } {
	const status = otpFailureStatus(err);
	if (status === 400 || status === 422) {
		if (attemptsSoFar + 1 >= MAX_OTP_ATTEMPTS) {
			return { fatal: true, errorKey: 'eid_easy_signing.otp_too_many_attempts' };
		}
		return { fatal: false, errorKey: 'eid_easy_signing.otp_incorrect_code' };
	}
	if (status !== undefined && status >= 500) {
		return { fatal: true, errorKey: DOCUMENT_SIGNING_FAILED_ERROR_KEY };
	}
	return { fatal: true, errorKey: toErrorKey(err) };
}

/**
 * eID Easy rejects a too-soon resend with a message like
 * "Please wait 21.620618 seconds before sending new codes". Extracts the wait
 * time (rounded up to whole seconds) so the UI can re-arm its resend countdown
 * to the server's actual remaining window. Returns null when the error is not a
 * resend rate-limit.
 */
export function parseOtpRateLimitSeconds(err: unknown): number | null {
	const rawMessage = extractRawMessage(err);
	if (!rawMessage) {
		return null;
	}
	// Bounded quantifiers (no unbounded \s*) to avoid ReDoS backtracking.
	const match = rawMessage.match(/wait\s{0,5}([\d.]+)\s{0,5}seconds?/i);
	if (!match?.[1]) {
		return null;
	}
	const seconds = Number(match[1]);
	if (!Number.isFinite(seconds) || seconds <= 0) {
		return null;
	}
	return Math.ceil(seconds);
}

/**
 * Smart-ID end-state codes that indicate the *user* intentionally refused
 * or cancelled the signing.  These should not be treated as unexpected
 * failures (e.g. no Sentry alert).
 */
const USER_CAUSED_SMART_ID_CODES = new Set([
	'USER_REFUSED',
	'USER_REFUSED_INTERACTION',
	'USER_REFUSED_DISPLAYTEXTANDPIN',
	'USER_REFUSED_VC_CHOICE',
	'USER_REFUSED_CONFIRMATIONMESSAGE',
	'USER_REFUSED_CONFIRMATIONMESSAGE_WITH_VC_CHOICE',
]);

/**
 * Returns `true` when the error represents a user-initiated cancellation
 * (popup closed, popup blocked, Smart-ID USER_REFUSED variants) rather than
 * an unexpected system failure.
 */
export const isUserInitiatedError = (err: unknown): boolean => {
	const rawMessage = extractRawMessage(err);
	if (!rawMessage) {
		return false;
	}

	if (detectPopupError(rawMessage)) {
		return true;
	}

	const smartIdCode = detectSmartIdCode(rawMessage);
	if (smartIdCode && USER_CAUSED_SMART_ID_CODES.has(smartIdCode)) {
		return true;
	}

	return false;
};

export const buildEndpointBase = (dealId: string, programmeId: string, instanceId: string) => {
	return `/deal/${dealId}/mip/${programmeId}/document-workflow/${instanceId}`;
};

export const FTN_CALLBACK_PATH = '/eid-easy/ftn-callback';

export const SMART_ID_TIMEOUT_MS = 3 * 60 * 1000;

export const OTP_SDK_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Wraps a promise with a timeout. If the promise doesn't settle before
 * `timeoutMs`, the returned promise rejects with `new Error(timeoutMessage)`.
 * The underlying promise keeps running but its result is discarded.
 */
export function withTimeout<T>(
	promise: Promise<T>,
	timeoutMs: number,
	timeoutMessage: string
): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<never>((_resolve, reject) => {
		timer = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
	});
	return Promise.race([promise, timeout]).finally(() => {
		if (timer) {
			clearTimeout(timer);
		}
	});
}

export const isEidEasySandbox = (baseUrl: string): boolean =>
	baseUrl === 'https://test.eideasy.com';

/**
 * Builds an eID Easy browser-client from a prepare result. Centralises the
 * repeated `createClient({ clientId, docId, language, sandbox })` boilerplate;
 * `redirectUri` is only needed by the OAuth2 redirect methods.
 */
export function makeEidEasyClient(
	prep: { clientId: string; docId: string; baseUrl: string },
	opts?: { redirectUri?: string }
): EidEasySdkClient {
	return createClient({
		clientId: prep.clientId,
		docId: prep.docId,
		language: 'en',
		sandbox: isEidEasySandbox(prep.baseUrl),
		...(opts?.redirectUri ? { redirectUri: opts.redirectUri } : {}),
	});
}

/**
 * Shared mechanism for eID Easy OAuth2 redirect signing methods (FTN,
 * Swedish/Norwegian BankID, Danish MitID). Opens the bank/app page in a new
 * browser tab and returns:
 *  - `redirect` — the handler to pass to the SDK's `<method>.start({ redirect })`;
 *  - `completed` — resolves when the FTN callback page posts
 *    `eid-easy-ftn-complete`, or rejects if the user closes the tab first;
 *  - `fail` — lets the caller surface SDK-side start failures (e.g. validation
 *    errors) so the flow shows an error instead of hanging on AWAITING_SIGN
 *    waiting for a tab that never opened.
 *
 * The reject messages intentionally keep the "popup" wording — `toErrorKey`
 * maps those phrases to the user-facing i18n keys.
 */
export function openSdkRedirectTab(): {
	redirect: (context: { data: { redirect_uri: string } }) => { data: null };
	completed: Promise<void>;
	fail: (error: unknown) => void;
} {
	let resolveCompleted: () => void;
	let rejectCompleted: (error: Error) => void;
	const completed = new Promise<void>((resolve, reject) => {
		resolveCompleted = resolve;
		rejectCompleted = reject;
	});

	// Tears down the message listener and close-poll. Set by `redirect` once they
	// exist; undefined before then, so it is safe to call from any settle path —
	// including `fail`, which can fire before `redirect` runs.
	let cleanup: (() => void) | undefined;

	const settleResolve = () => {
		cleanup?.();
		resolveCompleted();
	};
	const settleReject = (error: Error) => {
		cleanup?.();
		rejectCompleted(error);
	};

	const fail = (error: unknown) => {
		settleReject(error instanceof Error ? error : new Error(String(error)));
	};

	const redirect = (context: { data: { redirect_uri: string } }) => {
		// Open a new tab (no window features) rather than a popup window.
		const signingTab = window.open(context.data.redirect_uri, '_blank');

		if (!signingTab) {
			settleReject(new Error('Tab blocked. Please allow popups for this site.'));
			return { data: null };
		}

		const onMessage = (event: MessageEvent) => {
			if (
				event.origin === window.location.origin &&
				(event.data as Record<string, string>)?.type === 'eid-easy-ftn-complete'
			) {
				settleResolve();
			}
		};

		const checkClosed = setInterval(() => {
			if (signingTab.closed) {
				settleReject(new Error('Signing cancelled — popup was closed'));
			}
		}, 500);

		cleanup = () => {
			window.removeEventListener('message', onMessage);
			clearInterval(checkClosed);
		};

		window.addEventListener('message', onMessage);
		return { data: null };
	};

	return { redirect, completed, fail };
}

/**
 * FTN-specific convenience wrapper around `openSdkRedirectTab`: launches the
 * Finnish Trust Network signing flow in a new tab and resolves when it completes.
 */
export function startFtnTabSigning(client: EidEasySdkClient): Promise<void> {
	const { redirect, completed, fail } = openSdkRedirectTab();
	client.signature.ftnSignature.start({ countryCode: 'FI', redirect, fail });
	return completed;
}

/**
 * Launches the device-based Freja eID signature via the browser SDK and resolves
 * when the signer approves on their device (or rejects on timeout / SDK failure).
 * `onChallenge` receives the verification code to surface while awaiting approval.
 * Shared verbatim by the in-app and public signing flows.
 *
 * The SDK requires `email` and `countryCode` (and `docId`, from `createClient`) —
 * Freja identifies the signer by their Freja-account email; omitting `email` makes
 * `start()` throw synchronously with `Missing required setting "email"` before any
 * network call.
 */
export function runFrejaSdkSignature(
	client: EidEasySdkClient,
	{
		email,
		countryCode,
		onChallenge,
	}: {
		email: string;
		countryCode: string;
		onChallenge: (challenge: string) => void;
	}
): Promise<void> {
	const sdkPromise = new Promise<void>((resolve, reject) => {
		client.signature.frejaSimpleSignature.start({
			email,
			countryCode,
			started: (startResult) => {
				if (startResult.data?.challenge) {
					onChallenge(startResult.data.challenge);
				}
			},
			success: () => resolve(),
			fail: (failError) => {
				reject(ensureError(failError));
			},
		});
	});
	return withTimeout(sdkPromise, SMART_ID_TIMEOUT_MS, 'eid_easy_signing.error_timeout');
}

/** SDK start() options used by the OTP dispatch + validate calls. */
interface OtpStartOptions {
	smsToken?: string;
	emailToken?: string;
	started?: () => void;
	confirmationCodeRequest?: (
		context: unknown,
		resolve: (tokens: { smsToken?: string; emailToken?: string }) => void,
		reject: (err: unknown) => void
	) => void;
	success?: () => void;
	fail?: (err: unknown) => void;
}

/** Minimal slice of the eID Easy SDK client the OTP helpers need. */
export interface OtpCapableClient {
	signature: { otpSignature: { start: (options: OtpStartOptions) => unknown } };
}

/**
 * Dispatch-only OTP call. `start()` without a token triggers
 * `/api/signatures/start-signing`, which is what sends the SMS/email. Resolves
 * once `started` fires. `confirmationCodeRequest` is intentionally left
 * unresolved — validation happens via a separate `validateOtpCode()` call, so
 * this start() session is abandoned after dispatch.
 */
export function dispatchOtpCode(client: OtpCapableClient): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		client.signature.otpSignature.start({
			started: () => resolve(),
			confirmationCodeRequest: () => {
				/* never resolved — see doc comment */
			},
			fail: (failError) => reject(ensureError(failError)),
		});
	});
}

/**
 * Validate-only OTP call. Passing the token directly to `start()` makes the SDK
 * SKIP dispatch (its `if (!smsToken && !emailToken)` guard) and go straight to
 * `/api/signatures/non-qualified/complete`, validating against the same docId.
 * Re-callable per attempt with a fresh client — retries cost no new SMS.
 */
export function validateOtpCode(
	client: OtpCapableClient,
	channel: SignerContactType,
	code: string
): Promise<void> {
	const tokens = channel === SignerContactType.SMS ? { smsToken: code } : { emailToken: code };
	const sdkPromise = new Promise<void>((resolve, reject) => {
		client.signature.otpSignature.start({
			...tokens,
			success: () => resolve(),
			fail: (failError) => reject(ensureError(failError)),
		});
	});
	return withTimeout(sdkPromise, OTP_SDK_TIMEOUT_MS, 'eid_easy_signing.error_timeout');
}
